import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import toast from "react-hot-toast";

import { tg } from "@/constants/telegram";
import {
  useGetChatMessagesMutation,
  useGetChatMutation,
  useGetChatsMutation,
  useSendMessageMutation,
  useEditChatMutation,
} from "@/store/usersApi";
import {
  AdminChatPayload,
  AdminChatUser,
  ChatMessage,
} from "@/features/chats";

type AdminChatsContextValue = {
  initData?: string;
  chats: AdminChatUser[];
  chatsLoading: boolean;
  ensureChats: () => Promise<void>;
  isChatsLoaded: boolean;
  getChat: (userId: number) => Promise<AdminChatPayload | null>;
  getCachedChat: (userId: number) => AdminChatPayload | undefined;
  getMessages: (userId: number, force?: boolean) => Promise<ChatMessage[] | null>;
  getCachedMessages: (userId: number) => ChatMessage[] | undefined;
  sendSupportMessage: (userId: number, text?: string, image?: File) => Promise<void>;
  updateChatStage: (userId: number, stage: string) => Promise<void>;
  updateChatComment: (userId: number, comment: string) => void;
  isSending: boolean;
};

const AdminChatsContext = createContext<AdminChatsContextValue | undefined>(
  undefined,
);

export const useAdminChats = () => {
  const context = useContext(AdminChatsContext);

  if (!context) {
    throw new Error(
      "useAdminChats must be used within an AdminChatsProvider",
    );
  }

  return context;
};

export const AdminChatsProvider = ({ children }: { children: ReactNode }) => {
  const [initData, setInitData] = useState<string | undefined>(undefined);
  const [chats, setChats] = useState<AdminChatUser[]>([]);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [chatsLoaded, setChatsLoaded] = useState(false);
  const [chatCache, setChatCache] = useState<Record<number, AdminChatPayload>>(
    {},
  );
  const [messagesCache, setMessagesCache] = useState<
    Record<number, ChatMessage[]>
  >({});
  const [logsCache, setLogsCache] = useState<
    Record<number, ChatMessage[]>
  >({});

  const [getChats] = useGetChatsMutation();
  const [getChat] = useGetChatMutation();
  const [getChatMessages] = useGetChatMessagesMutation();
  const [sendMessageMutation, { isLoading: isSending }] =
    useSendMessageMutation();
  const [editChat] = useEditChatMutation();

  useEffect(() => {
    if (!initData) {
      tg.ready();
      setInitData(tg?.initData);

      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.expand();
      }
    }
  }, [initData]);

  const ensureChats = useCallback(async () => {
    if (!initData || chatsLoading || chatsLoaded) {
      return;
    }

    setChatsLoading(true);

    try {
      const result = await getChats().unwrap();
      setChats(result);
      setChatsLoaded(true);
    } catch (error: any) {
      if (error?.status === 403) {
        window.location.href = "/";
        return;
      }

      toast.error("Не удалось загрузить список чатов");
      console.error("getChats error", error);
    } finally {
      setChatsLoading(false);
    }
  }, [chatsLoaded, chatsLoading, getChats, initData]);

  const fetchChat = useCallback(
    async (userId: number): Promise<AdminChatPayload | null> => {
      if (chatCache[userId]) {
        return chatCache[userId];
      }

      if (!initData) {
        return null;
      }

      try {
        const data = await getChat({
          user_id: userId,
        }).unwrap();

        setChatCache((prev) => ({ ...prev, [userId]: data }));
        return data;
      } catch (error: any) {
        if (error?.status === 403) {
          window.location.href = "/";
          return null;
        }

        toast.error("Не удалось загрузить данные пользователя");
        console.error("getChat error", error);
        return null;
      }
    },
    [chatCache, getChat, initData],
  );

  const fetchMessages = useCallback(
    async (
      userId: number,
      force = false,
    ): Promise<ChatMessage[] | null> => {
      if (!force && messagesCache[userId]) {
        return messagesCache[userId];
      }

      if (!initData) {
        return null;
      }

      try {
        const data = await getChatMessages({
          user_id: userId,
        }).unwrap();

        setMessagesCache((prev) => ({ ...prev, [userId]: data }));
        return data;
      } catch (error: any) {
        if (error?.status === 403) {
          window.location.href = "/";
          return null;
        }

        toast.error("Не удалось загрузить сообщения");
        console.error("getChatMessages error", error);
        return null;
      }
    },
    [getChatMessages, initData, messagesCache],
  );

  const sendSupportMessage = useCallback(
    async (userId: number, text?: string, image?: File) => {
      if (!initData) {
        return;
      }

      if (!text && !image) {
        toast.error("Необходимо указать текст или изображение");
        return;
      }

      try {
        await sendMessageMutation({
          user_id: userId,
          msg: text,
          image: image,
        }).unwrap();

        // Формируем контент сообщения для отображения
        let messageContent = text || "";
        if (image) {
          // Создаем временный URL для превью изображения
          const imageUrl = URL.createObjectURL(image);
          if (messageContent) {
            messageContent = `${messageContent}\n<img>${imageUrl}</img>`;
          } else {
            messageContent = `<img>${imageUrl}</img>`;
          }
        }

        setMessagesCache((prev) => {
          const prevMessages = prev[userId] ?? [];
          const newMessage: ChatMessage = {
            id: Date.now(),
            chat_id: userId,
            user_id: userId,
            author: "support-user",
            content: messageContent,
            of_date: new Date().toISOString(),
          };

          return {
            ...prev,
            [userId]: [...prevMessages, newMessage],
          };
        });
      } catch (error: any) {
        if (error?.status === 403) {
          window.location.href = "/";
          return;
        }

        toast.error("Не удалось отправить сообщение");
        console.error("sendMessage error", error);
      }
    },
    [initData, sendMessageMutation],
  );

  const updateChatStage = useCallback(
    async (userId: number, stage: string) => {
      if (!initData) {
        return;
      }

      try {
        await editChat({
          user_id: userId,
          data: { stage },
        }).unwrap();

        // Обновляем stage в списке чатов
        setChats((prev) =>
          prev.map((chat) =>
            chat.user_id === userId ? { ...chat, stage } : chat
          )
        );

        // Обновляем stage в кэше чата, если он есть
        setChatCache((prev) => {
          const cached = prev[userId];
          if (cached) {
            return {
              ...prev,
              [userId]: { ...cached, stage },
            };
          }
          return prev;
        });

        toast.success("Статус обновлен");
      } catch (error: any) {
        if (error?.status === 403) {
          window.location.href = "/";
          return;
        }

        toast.error("Не удалось обновить статус");
        console.error("editChat error", error);
      }
    },
    [initData, editChat],
  );

  const updateChatComment = useCallback(
    (userId: number, comment: string) => {
      // Обновляем comment в кэше чата, если он есть
      setChatCache((prev) => {
        const cached = prev[userId];
        if (cached) {
          return {
            ...prev,
            [userId]: { ...cached, coment: comment, updated_at: new Date().toISOString() },
          };
        }
        return prev;
      });
    },
    [],
  );

  const value = useMemo<AdminChatsContextValue>(
    () => ({
      initData,
      chats,
      chatsLoading,
      ensureChats,
      isChatsLoaded: chatsLoaded,
      getChat: fetchChat,
      getCachedChat: (userId: number) => chatCache[userId],
      getMessages: fetchMessages,
      getCachedMessages: (userId: number) => messagesCache[userId],
      sendSupportMessage,
      updateChatStage,
      updateChatComment,
      isSending,
    }),
    [
      initData,
      chats,
      chatsLoading,
      ensureChats,
      chatsLoaded,
      fetchChat,
      chatCache,
      fetchMessages,
      messagesCache,
      sendSupportMessage,
      updateChatStage,
      updateChatComment,
      isSending,
    ],
  );

  return (
    <AdminChatsContext.Provider value={value}>
      {children}
    </AdminChatsContext.Provider>
  );
};



















